import { useCallback, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { RHFStringInput } from "@impero-com/common/components/form/rhfFields/rhfStringInput";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { emitNotification, Divider, FormLabel } from "@impero-com/ui";
import { useForm, UseFormReturn } from "react-hook-form";

import {
    ControlProgramTagCategoryView,
    NewControlProgramTagCategoryWithTags,
} from "api/generated/model";
import { Drawer } from "components/drawer";
import { CancelConfirmationDialog } from "org/tags/components/CancelConfirmationDialog";

import { useCreateControlProgramTagCategoryMutation } from "../queries/useCreateControlProgramTagCategoryMutation";

import { CreateDrawerFooter } from "./components/CreateDrawerFooter";
import { ControlProgramTagManagementCreateList } from "./ControlProgramTagManagementCreateList";
import {
    ControlProgramTagCategoryCreateFormSchema,
    refinedNewNameSchema,
    useControlProgramTagCategoryCreateFormSchema,
} from "./useControlProgramTagCategoryFormSchema";
interface DrawerProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    tagCategories: ControlProgramTagCategoryView[];
}

export function CreateControlProgramTagCategoryDrawer({
    isOpen,
    setIsOpen,
    tagCategories,
}: DrawerProps) {
    const { t } = useTranslation();
    const { mutateAsync: createTagCategory } =
        useCreateControlProgramTagCategoryMutation();
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
    const [tagNames, setTagNames] = useState<string[]>([]);
    const existingCategoryNames = useMemo(
        () => tagCategories.map((category) => category.name),
        [tagCategories]
    );

    const schema = useControlProgramTagCategoryCreateFormSchema(
        tagNames,
        existingCategoryNames
    );
    const form = useForm<ControlProgramTagCategoryCreateFormSchema>({
        resolver: zodResolver(schema),
        mode: "onBlur",
        defaultValues: {
            categoryName: "",
            newTagName: "",
            tags: [],
        },
    });

    const { reset, getFieldState, getValues } = form;

    const onSubmit = useCallback(
        async (data: ControlProgramTagCategoryCreateFormSchema) => {
            const tagNames: string[] = data.tags.map((tag) => tag.name);
            const payload: NewControlProgramTagCategoryWithTags = {
                categoryName: data.categoryName,
                tagNames,
            };
            try {
                await createTagCategory({ data: payload });
                setIsOpen(false);
                reset();
                emitNotification(
                    "success",
                    t(
                        "TagsList_CreateCategoryDrawer_Notification_TagCategoryCreated",
                        { tagCategoryName: payload.categoryName }
                    )
                );
            } catch (error) {
                console.error("Failed to create tag category:", error);
                emitNotification(
                    "error",
                    t(
                        "TagsList_CreateCategoryDrawer_Notification_CreateTagCategoryFailed"
                    )
                );
            }
        },
        [createTagCategory, setIsOpen, reset, t]
    );

    const onCancel = useCallback(() => {
        reset();
        setIsOpen(false);
        setShowCancelConfirmation(false);
    }, [reset, setIsOpen]);

    const onCancelWithConfirmation = useCallback(() => {
        const isCategoryDirty = getFieldState("categoryName").isDirty;
        const tags = getValues("tags");
        const hasTags = tags?.length > 0;
        const isDirty = isCategoryDirty || hasTags;
        if (isDirty) {
            setShowCancelConfirmation(true);
        } else {
            onCancel();
        }
    }, [getFieldState, onCancel, getValues]);

    return (
        <>
            <Drawer
                buildHeader={() => t("TagsList_CreateCategoryDrawer_Header")}
                buildFooter={() => (
                    <CreateDrawerFooter
                        form={form}
                        onSubmit={onSubmit}
                        onCancelWithConfirmation={onCancelWithConfirmation}
                    />
                )}
                onCancel={onCancelWithConfirmation}
                open={isOpen}
            >
                <CreateForm
                    form={form}
                    existingCategoryNames={existingCategoryNames}
                    setTagNames={setTagNames}
                />
            </Drawer>
            <CancelConfirmationDialog
                open={showCancelConfirmation}
                onConfirm={onCancel}
                onAbort={() => setShowCancelConfirmation(false)}
            />
        </>
    );
}

function CreateForm({
    form,
    existingCategoryNames,
    setTagNames,
}: {
    form: UseFormReturn<ControlProgramTagCategoryCreateFormSchema>;
    existingCategoryNames: string[];
    setTagNames: React.Dispatch<React.SetStateAction<string[]>>;
}) {
    const { t } = useTranslation();
    const { control } = form;
    return (
        <>
            <RHFStringInput
                label={t("TagsList_EditCategoryDrawer_Field_CategoryName")}
                control={control}
                name="categoryName"
                schema={refinedNewNameSchema(t, existingCategoryNames)}
            />
            <Divider />
            <FormLabel>
                {t("TagsList_EditCategoryDrawer_Field_ProgramTags")}
            </FormLabel>
            <ControlProgramTagManagementCreateList
                form={form}
                setTagNames={setTagNames}
            />
        </>
    );
}
